'use client';

/**
 * Wishlist Hooks
 * 
 * Provides wishlist functionality for customers.
 * Uses the CustomerAuthProvider for API access.
 * 
 * IMPORTANT: useWishlistStatus uses a shared WishlistStatusProvider context
 * to batch all wishlist checks into a single API call, avoiding N+1 requests
 * when multiple WishlistButton components are rendered on the same page.
 */

import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import type {
    AddToWishlistRequest,
    Wishlist,
    WishlistEntityType,
    WishlistItem,
    WishlistWithItems,
} from '@/types/customer';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    type ReactNode,
} from 'react';

// ============================================================================
// Types
// ============================================================================

export interface UseWishlistReturn {
  /** All wishlists for the current customer */
  wishlists: Wishlist[];
  /** Whether wishlists are loading */
  isLoading: boolean;
  /** Error if any */
  error: string | null;
  /** Refresh wishlists */
  refresh: () => Promise<void>;
  /** Get a specific wishlist with items */
  getWishlist: (wishlistId: string) => Promise<WishlistWithItems | null>;
  /** Create a new wishlist */
  createWishlist: (name: string, description?: string) => Promise<Wishlist | null>;
  /** Delete a wishlist */
  deleteWishlist: (wishlistId: string) => Promise<boolean>;
  /** Add item to a wishlist */
  addToWishlist: (wishlistId: string, data: AddToWishlistRequest) => Promise<WishlistItem | null>;
  /** Quick add to default wishlist */
  quickAdd: (entityType: WishlistEntityType, entityId: string, notes?: string) => Promise<boolean>;
  /** Remove item from wishlist */
  removeFromWishlist: (wishlistId: string, itemId: string) => Promise<boolean>;
  /** Check if an entity is in any wishlist */
  isInWishlist: (entityType: WishlistEntityType, entityId: string) => Promise<boolean>;
}

// ============================================================================
// Hook
// ============================================================================

export function useWishlist(): UseWishlistReturn {
  const { getCustomerClient, isAuthenticated } = useCustomerAuth();
  const [wishlists, setWishlists] = useState<Wishlist[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch wishlists on mount
  const refresh = useCallback(async () => {
    const client = getCustomerClient();
    if (!client) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await client.getWishlists();
      setWishlists(response?.items || []);
    } catch (err) {
      // Log the error but handle gracefully for API issues
      const errorMessage = err instanceof Error ? err.message : 'Failed to load wishlists';
      console.error('[useWishlist] Failed to fetch wishlists:', err);
      
      // Don't show error to user for API availability issues
      if (errorMessage.includes('API server unavailable') || 
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('Network')) {
        setWishlists([]);
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [getCustomerClient]);

  // Load wishlists when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      setWishlists([]);
    }
  }, [isAuthenticated, refresh]);

  // Get a specific wishlist with items
  const getWishlist = useCallback(async (wishlistId: string): Promise<WishlistWithItems | null> => {
    const client = getCustomerClient();
    if (!client) return null;

    try {
      return await client.getWishlist(wishlistId);
    } catch (err) {
      console.error('[useWishlist] Failed to get wishlist:', err);
      return null;
    }
  }, [getCustomerClient]);

  // Create a new wishlist
  const createWishlist = useCallback(async (
    name: string,
    description?: string
  ): Promise<Wishlist | null> => {
    const client = getCustomerClient();
    if (!client) return null;

    try {
      const wishlist = await client.createWishlist({ 
        name, 
        ...(description ? { description } : {}),
      });
      setWishlists((prev) => [...prev, wishlist]);
      return wishlist;
    } catch (err) {
      console.error('[useWishlist] Failed to create wishlist:', err);
      setError(err instanceof Error ? err.message : 'Failed to create wishlist');
      return null;
    }
  }, [getCustomerClient]);

  // Delete a wishlist
  const deleteWishlist = useCallback(async (wishlistId: string): Promise<boolean> => {
    const client = getCustomerClient();
    if (!client) return false;

    try {
      await client.deleteWishlist(wishlistId);
      setWishlists((prev) => prev.filter((w) => w.id !== wishlistId));
      return true;
    } catch (err) {
      console.error('[useWishlist] Failed to delete wishlist:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete wishlist');
      return false;
    }
  }, [getCustomerClient]);

  // Add item to a wishlist
  const addToWishlist = useCallback(async (
    wishlistId: string,
    data: AddToWishlistRequest
  ): Promise<WishlistItem | null> => {
    const client = getCustomerClient();
    if (!client) return null;

    try {
      const item = await client.addToWishlist(wishlistId, data);
      // Update wishlist item count
      setWishlists((prev) =>
        prev.map((w) =>
          w.id === wishlistId ? { ...w, itemCount: w.itemCount + 1 } : w
        )
      );
      return item;
    } catch (err) {
      console.error('[useWishlist] Failed to add to wishlist:', err);
      setError(err instanceof Error ? err.message : 'Failed to add to wishlist');
      return null;
    }
  }, [getCustomerClient]);

  // Quick add to default wishlist
  const quickAdd = useCallback(async (
    entityType: WishlistEntityType,
    entityId: string,
    notes?: string
  ): Promise<boolean> => {
    const client = getCustomerClient();
    if (!client) return false;

    try {
      await client.quickAddToWishlist({ 
        entityType, 
        entityId, 
        ...(notes ? { notes } : {}),
      });
      // Refresh to get updated counts
      await refresh();
      return true;
    } catch (err) {
      console.error('[useWishlist] Failed to quick add:', err);
      setError(err instanceof Error ? err.message : 'Failed to save to wishlist');
      return false;
    }
  }, [getCustomerClient, refresh]);

  // Remove item from wishlist
  const removeFromWishlist = useCallback(async (
    wishlistId: string,
    itemId: string
  ): Promise<boolean> => {
    const client = getCustomerClient();
    if (!client) return false;

    try {
      await client.removeFromWishlist(wishlistId, itemId);
      // Refresh to get accurate counts from server instead of manually decrementing
      await refresh();
      return true;
    } catch (err) {
      console.error('[useWishlist] Failed to remove from wishlist:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove from wishlist');
      return false;
    }
  }, [getCustomerClient, refresh]);

  // Check if an entity is in any wishlist
  const isInWishlist = useCallback(async (
    entityType: WishlistEntityType,
    entityId: string
  ): Promise<boolean> => {
    const client = getCustomerClient();
    if (!client) return false;

    try {
      const result = await client.isInWishlist(entityType, entityId);
      return result.isInWishlist;
    } catch (err) {
      console.error('[useWishlist] Failed to check wishlist status:', err);
      return false;
    }
  }, [getCustomerClient]);

  return {
    wishlists,
    isLoading,
    error,
    refresh,
    getWishlist,
    createWishlist,
    deleteWishlist,
    addToWishlist,
    quickAdd,
    removeFromWishlist,
    isInWishlist,
  };
}

// ============================================================================
// Wishlist Status Context (Batch Provider)
// ============================================================================

/**
 * Entity key for wishlist status lookups
 */
function makeEntityKey(entityType: WishlistEntityType, entityId: string): string {
  return `${entityType}:${entityId}`;
}

/**
 * Wishlist status entry for a single entity
 */
interface WishlistStatusEntry {
  isInWishlist: boolean;
  wishlistIds: string[];
}

/**
 * Context for sharing wishlist status across all WishlistButton components.
 * This avoids N+1 API calls by fetching all wishlists once and deriving
 * per-entity status locally.
 */
interface WishlistStatusContextType {
  /** Map of entityKey -> status */
  statusMap: Map<string, WishlistStatusEntry>;
  /** Whether the initial load is in progress */
  isLoading: boolean;
  /** Whether there was an auth/API error (stops retries) */
  hasError: boolean;
  /** Refresh all wishlist data */
  refresh: () => Promise<void>;
  /** Optimistically update a single entity's status */
  optimisticUpdate: (entityType: WishlistEntityType, entityId: string, status: WishlistStatusEntry) => void;
}

const WishlistStatusContext = createContext<WishlistStatusContextType | null>(null);

/**
 * WishlistStatusProvider
 * 
 * Wraps a page/section that contains multiple WishlistButton components.
 * Fetches all wishlists once and provides per-entity status via context.
 * 
 * This prevents the N+1 problem where each WishlistButton independently
 * calls /api/customer/proxy to check wishlist status.
 */
export function WishlistStatusProvider({ children }: { children: ReactNode }) {
  const { getCustomerClient, isAuthenticated } = useCustomerAuth();
  const [statusMap, setStatusMap] = useState<Map<string, WishlistStatusEntry>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const fetchedRef = useRef(false);

  const refresh = useCallback(async () => {
    const client = getCustomerClient();
    if (!client) return;

    setIsLoading(true);
    setHasError(false);

    try {
      // Fetch all wishlists with their items in a single call
      const response = await client.getWishlists();
      const wishlists = response?.items || [];
      
      // Build a map of entityKey -> { isInWishlist, wishlistIds }
      const newMap = new Map<string, WishlistStatusEntry>();
      
      // For each wishlist, fetch its items to build the status map
      // We fetch wishlists in parallel for speed
      const wishlistDetails = await Promise.allSettled(
        wishlists.map(w => client.getWishlist(w.id))
      );
      
      for (const result of wishlistDetails) {
        if (result.status === 'fulfilled') {
          const wishlist = result.value;
          for (const item of wishlist.items) {
            const key = makeEntityKey(item.entityType as WishlistEntityType, item.entityId);
            const existing = newMap.get(key);
            if (existing) {
              existing.wishlistIds.push(wishlist.id);
            } else {
              newMap.set(key, {
                isInWishlist: true,
                wishlistIds: [wishlist.id],
              });
            }
          }
        }
      }
      
      setStatusMap(newMap);
    } catch (err) {
      // Use warn for auth failures - common when token invalid or Route Royal Supabase config mismatch
      const errorObj = err as Error & { code?: string; status?: number };
      const isAuthFailure = errorObj?.status === 401 || errorObj?.status === 403;
      (isAuthFailure ? console.warn : console.error)('[WishlistStatusProvider] Failed to fetch wishlists:', err);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [getCustomerClient]);

  const optimisticUpdate = useCallback((
    entityType: WishlistEntityType,
    entityId: string,
    status: WishlistStatusEntry
  ) => {
    setStatusMap(prev => {
      const next = new Map(prev);
      const key = makeEntityKey(entityType, entityId);
      if (status.isInWishlist) {
        next.set(key, status);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);

  // Fetch wishlists when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setStatusMap(new Map());
      fetchedRef.current = false;
      return;
    }

    // Only fetch once per authentication session
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      refresh();
    }
  }, [isAuthenticated, refresh]);

  return (
    <WishlistStatusContext.Provider value={{ statusMap, isLoading, hasError, refresh, optimisticUpdate }}>
      {children}
    </WishlistStatusContext.Provider>
  );
}

/**
 * Hook to access the shared wishlist status context.
 * Falls back to individual API calls if no provider is present.
 */
function useWishlistStatusContext(): WishlistStatusContextType | null {
  return useContext(WishlistStatusContext);
}

// ============================================================================
// Wishlist Status Hook
// ============================================================================

/**
 * Hook to check if a specific entity is in a wishlist.
 * Useful for "heart" buttons on property cards.
 * 
 * When used inside a WishlistStatusProvider (recommended), this hook
 * reads from the shared status map — no individual API calls needed.
 * 
 * When used without a provider, falls back to individual API calls
 * with circuit-breaker logic to prevent cascading failures.
 */
export function useWishlistStatus(
  entityType: WishlistEntityType,
  entityId: string
): {
  isInWishlist: boolean;
  isLoading: boolean;
  toggle: () => Promise<void>;
} {
  const { getCustomerClient, isAuthenticated } = useCustomerAuth();
  const context = useWishlistStatusContext();
  
  // Local state for standalone mode (no provider) or for toggle operations
  const [localIsInWishlist, setLocalIsInWishlist] = useState(false);
  const [localWishlistIds, setLocalWishlistIds] = useState<string[]>([]);
  const [localIsLoading, setLocalIsLoading] = useState(false);
  const [toggleInProgress, setToggleInProgress] = useState(false);

  // Determine if we're using the context or standalone mode
  const hasContext = context !== null;

  // Get status from context if available
  const entityKey = makeEntityKey(entityType, entityId);
  const contextEntry = context?.statusMap.get(entityKey);
  
  // Use context values when available, local state otherwise
  const isInWishlist = toggleInProgress 
    ? localIsInWishlist 
    : hasContext 
      ? (contextEntry?.isInWishlist ?? false)
      : localIsInWishlist;
  
  const wishlistIds = hasContext 
    ? (contextEntry?.wishlistIds ?? [])
    : localWishlistIds;
  
  const isLoading = hasContext ? (context.isLoading && !toggleInProgress) : localIsLoading;

  // Standalone mode: check status on mount (only if no context provider)
  useEffect(() => {
    if (hasContext || !isAuthenticated) {
      if (!isAuthenticated) {
        setLocalIsInWishlist(false);
        setLocalWishlistIds([]);
      }
      return;
    }

    let isMounted = true;

    const checkStatus = async () => {
      const client = getCustomerClient();
      if (!client) return;

      setLocalIsLoading(true);
      try {
        const result = await client.isInWishlist(entityType, entityId);
        if (isMounted) {
          setLocalIsInWishlist(result.isInWishlist);
          setLocalWishlistIds(result.wishlistIds);
        }
      } catch (err) {
        // Use warn for auth failures (403/401) - common when token invalid or Route Royal misconfigured
        const errorObj = err as Error & { code?: string; status?: number };
        const isAuthFailure = errorObj.status === 401 || errorObj.status === 403 || errorObj.code === 'UPSTREAM_FORBIDDEN' || errorObj.code === 'UPSTREAM_UNAUTHORIZED';
        const logFn = isAuthFailure ? console.warn : console.error;
        logFn('[useWishlistStatus] Check failed:', {
          error: err instanceof Error ? err.message : String(err),
          entityType,
          entityId,
        });
        // No retry — if the API is down, retrying just makes it worse
      } finally {
        if (isMounted) {
          setLocalIsLoading(false);
        }
      }
    };

    checkStatus();

    return () => {
      isMounted = false;
    };
  }, [hasContext, isAuthenticated, getCustomerClient, entityType, entityId]);

  // Toggle wishlist status with optimistic update and rollback on failure
  const toggle = useCallback(async () => {
    const client = getCustomerClient();
    if (!client) return;

    // Capture previous state for rollback
    const previousIsInWishlist = isInWishlist;
    const previousWishlistIds = [...wishlistIds];

    setToggleInProgress(true);
    setLocalIsLoading(true);
    try {
      if (isInWishlist && wishlistIds.length > 0) {
        // Optimistic update: remove immediately
        setLocalIsInWishlist(false);
        if (hasContext) {
          context.optimisticUpdate(entityType, entityId, { isInWishlist: false, wishlistIds: [] });
        }

        // Get the wishlist and find the item to remove
        const firstWishlistId = wishlistIds[0];
        if (firstWishlistId) {
          const wishlist = await client.getWishlist(firstWishlistId);
          const item = wishlist.items.find(
            (i) => i.entityType === entityType && i.entityId === entityId
          );
          if (item) {
            await client.removeFromWishlist(firstWishlistId, item.id);
          }
        }
      } else {
        // Optimistic update: add immediately
        setLocalIsInWishlist(true);

        // Add to default wishlist
        const result = await client.quickAddToWishlist({ entityType, entityId });
        setLocalWishlistIds([result.wishlistId]);
        if (hasContext) {
          context.optimisticUpdate(entityType, entityId, { isInWishlist: true, wishlistIds: [result.wishlistId] });
        }
      }
    } catch (err) {
      console.error('[useWishlistStatus] Toggle failed, rolling back:', err);
      // Rollback to previous state on failure
      setLocalIsInWishlist(previousIsInWishlist);
      setLocalWishlistIds(previousWishlistIds);
      if (hasContext) {
        context.optimisticUpdate(entityType, entityId, {
          isInWishlist: previousIsInWishlist,
          wishlistIds: previousWishlistIds,
        });
      }
    } finally {
      setToggleInProgress(false);
      setLocalIsLoading(false);
    }
  }, [getCustomerClient, isInWishlist, wishlistIds, entityType, entityId, hasContext, context]);

  return { isInWishlist, isLoading, toggle };
}

export default useWishlist;
